import { type FC, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import { useCommunityMembership } from '~/routes/community/_hooks/useCommunityMembership';

import type { UseCommentThreadReturn } from '../_hooks/useCommentThread';
import { Comment } from './Comment';

const INITIAL_VISIBLE_COUNT = 2;

interface CommentThreadProps {
  postId: string;
  thread: UseCommentThreadReturn;
  isExpanded: boolean;
  commentsState: 'open' | 'closed' | 'hidden';
  onExpand: () => void;
  onShowFullThread?: () => void;
  onCommentCreated?: () => void;
  spaceSlug?: string;
}

export const CommentThread: FC<CommentThreadProps> = ({
  postId,
  thread,
  isExpanded,
  commentsState,
  onExpand,
  onShowFullThread,
  onCommentCreated,
  spaceSlug,
}) => {
  const { t } = useTranslation();
  const { permissions } = useCommunityMembership();
  const {
    comments,
    focusedCommentId,
    removedIds,
    hasMore,
    hasPrevious,
    isLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isPreviewing,
    isLoadingFullThread,
    remainingCount,
  } = thread;

  // A preview lists everything it holds; the oldest-two collapse only applies to a fetched thread.
  const expanded = isExpanded || isPreviewing || Boolean(focusedCommentId);

  const scrolledToRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusedCommentId || scrolledToRef.current === focusedCommentId) return;
    const element = document.getElementById(focusedCommentId);
    if (!element) return;
    scrolledToRef.current = focusedCommentId;
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusedCommentId]);

  const preservePositionRef = useRef<{ id: string; top: number } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: must re-run when comments change, not read them
  useLayoutEffect(() => {
    const saved = preservePositionRef.current;
    if (!saved) return;
    preservePositionRef.current = null;
    const element = document.getElementById(saved.id);
    if (!element) return;
    const delta = element.getBoundingClientRect().top - saved.top;
    if (delta !== 0) window.scrollBy(0, delta);
  }, [comments]);

  const visibleComments = useMemo(() => {
    const filtered = comments.filter((comment) => {
      if (!removedIds.has(comment.id)) return true;
      const liveReplies = (comment.sub_comments ?? []).filter((sub) => !removedIds.has(sub.id));
      return liveReplies.length > 0;
    });

    if (expanded) return filtered;
    return filtered.slice(0, INITIAL_VISIBLE_COUNT);
  }, [comments, removedIds, expanded]);

  const totalVisible = comments.filter((c) => !removedIds.has(c.id)).length;
  const hiddenCount = totalVisible - visibleComments.length;

  if (comments.length === 0) {
    if (isLoading) {
      return <div className="py-4 px-4 text-[15px] text-black/40">{t('community.comments.loading')}...</div>;
    }
    if (commentsState !== 'open') return null;
    const emptyKey = permissions.canComment ? 'community.comments.no_comments_cta' : 'community.comments.no_comments';
    return <p className="py-4 px-4 text-[15px] text-black/40">{t(emptyKey)}</p>;
  }

  const handleLoadMore = () => {
    if (isLoadingFullThread) return;
    if (isPreviewing) {
      onShowFullThread?.();
      onExpand();
    } else if (!expanded) {
      onExpand();
    } else {
      thread.loadMore();
    }
  };

  const handleLoadEarlier = () => {
    const firstId = visibleComments[0]?.id;
    const element = firstId ? document.getElementById(firstId) : null;
    preservePositionRef.current = firstId && element ? { id: firstId, top: element.getBoundingClientRect().top } : null;
    thread.loadEarlier();
  };

  const showsPreviewButton = isPreviewing || isLoadingFullThread;
  const showLoadMore = showsPreviewButton ? remainingCount > 0 : hiddenCount > 0 || (expanded && hasMore);
  const isLoadMoreBusy = isFetchingNextPage || isLoadingFullThread;
  const loadMoreLabel = (() => {
    if (isFetchingNextPage) return t('community.comments.loading');
    if (showsPreviewButton) return t('community.comments.show_more_count', { count: remainingCount });
    if (hiddenCount > 0) return t('community.comments.show_more_count', { count: hiddenCount });
    return t('community.comments.load_more');
  })();

  return (
    <div className="flex flex-col">
      {hasPrevious && (
        <button
          type="button"
          onClick={handleLoadEarlier}
          disabled={isFetchingPreviousPage}
          className={cn(
            'flex items-center py-4 px-4 w-full',
            'font-medium text-[15px] leading-4 text-black opacity-60 hover:opacity-80 transition-opacity',
            { 'opacity-30': isFetchingPreviousPage },
          )}
        >
          {isFetchingPreviousPage ? t('community.comments.loading') : t('community.comments.load_earlier')}
        </button>
      )}
      {visibleComments.map((comment) => {
        const isRemoved = removedIds.has(comment.id);
        const liveSubcomments = (comment.sub_comments ?? []).filter((sub) => !removedIds.has(sub.id));
        const hasSubcomments = liveSubcomments.length > 0;

        return (
          <div key={comment.id}>
            <Comment
              comment={comment}
              postId={postId}
              commentsState={commentsState}
              isRemoved={isRemoved}
              hasReplies={hasSubcomments}
              highlighted={comment.id === focusedCommentId}
              onReply={isRemoved ? undefined : (reply) => thread.addReply(comment.id, reply)}
              onDelete={isRemoved ? undefined : (options) => thread.deleteComment(comment.id, options)}
              onUpdate={isRemoved ? undefined : thread.updateComment}
              onCommentCreated={onCommentCreated}
              spaceSlug={spaceSlug}
            />

            {hasSubcomments && (
              <div className="relative">
                <div className="absolute left-9 -top-0.5 h-8 w-px bg-black/10" />
                {liveSubcomments.map((sub, index) => (
                  <div key={sub.id} className="relative flex items-start gap-2">
                    {index < liveSubcomments.length - 1 && (
                      <div className="absolute bottom-0 left-9 top-[7px] w-px bg-black/10" />
                    )}
                    <div className="relative ml-4 h-10 w-10 shrink-0 overflow-hidden">
                      <div className="absolute left-5 -top-px h-9 w-5">
                        <div className="absolute left-0 top-0 h-5 w-px bg-black/10" />
                        <div className="absolute bottom-0 left-0 h-4 w-5 rounded-bl-[16px] border-b border-l border-black/10" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Comment
                        comment={sub}
                        postId={postId}
                        commentsState={commentsState}
                        isReply
                        highlighted={sub.id === focusedCommentId}
                        onDelete={(options) => thread.deleteComment(sub.id, options)}
                        onUpdate={thread.updateComment}
                        onCommentCreated={onCommentCreated}
                        spaceSlug={spaceSlug}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {showLoadMore && (
        <button
          type="button"
          onClick={handleLoadMore}
          disabled={isLoadMoreBusy}
          className={cn(
            'flex items-center py-4 px-4 w-full',
            'font-medium text-[15px] leading-4 text-black transition-opacity',
            { 'opacity-60 hover:opacity-80': !isLoadMoreBusy, 'opacity-30': isLoadMoreBusy },
          )}
        >
          {loadMoreLabel}
        </button>
      )}
    </div>
  );
};
